import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import {
  FaReceipt,
  FaSearch,
  FaPrint,
  FaBan,
  FaUndo,
  FaTimes,
  FaFileAlt,
  FaShieldAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import PrintableFeeReceipt from "./PrintableFeeReceipt";

export default function FeeReceiptsRegisterPage() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const { token } = useAuth();

  // Print modal state
  const [printingReceipt, setPrintingReceipt] = useState(null);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Receipt_${printingReceipt?.receiptNumber || ""}`,
    pageStyle: `@page { size: A4 portrait; margin: 10mm; }`,
  });

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${server}/fee-collection/receipts?search=${searchTerm}&status=${selectedStatus}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setReceipts(res.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load fee receipts register");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchReceipts();
  }, [token, selectedStatus]);

  const triggerPrint = (rc) => {
    setPrintingReceipt({ ...rc, isDuplicateCopy: true });
    setTimeout(() => {
      handlePrint();
    }, 150);
  };

  const handleCancelReceipt = async (id) => {
    const reason = window.prompt("Enter reason for cancelling this receipt:");
    if (reason === null) return;

    try {
      const res = await axios.post(
        `${server}/fee-collection/receipts/${id}/cancel?reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success("Receipt cancelled successfully!");
        fetchReceipts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to cancel receipt");
    }
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-950 text-purple-600 dark:text-purple-400 rounded-xl shadow-xs">
            <FaReceipt size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Fee Receipts Register</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Audit trail of all issued receipts, cancellations, and duplicate re-printing</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-black text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950 px-3 py-1 rounded-xl border border-purple-200 dark:border-purple-900">
            {receipts.length} Total Receipts
          </span>
        </div>
      </div>

      {/* Compact Filter Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3.5 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 flex flex-col md:flex-row gap-3 justify-between items-center">
        {/* Search */}
        <div className="relative w-full md:w-96">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
          <input
            type="text"
            placeholder="Search Receipt No, Student Name, Reg No..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchReceipts()}
            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none focus:border-indigo-500"
          />
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mr-1">Status:</span>
          {["ALL", "Valid", "Cancelled", "Refunded"].map((st) => (
            <button
              key={st}
              onClick={() => setSelectedStatus(st)}
              className={`px-3 py-1 rounded-xl text-xs font-black transition ${
                selectedStatus === st
                  ? "bg-purple-600 text-white shadow-xs"
                  : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-slate-700 hover:bg-gray-200"
              }`}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* High-Density Receipts Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 border-b border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400 text-[10px] uppercase font-black tracking-wider">
                <th className="p-2.5">Receipt No</th>
                <th className="p-2.5">Student Name</th>
                <th className="p-2.5">Reg No</th>
                <th className="p-2.5">Date</th>
                <th className="p-2.5 text-right">Amount Paid</th>
                <th className="p-2.5">Payment Mode</th>
                <th className="p-2.5 text-center">Status</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-purple-600 font-bold">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2"></div>
                    Loading receipts register...
                  </td>
                </tr>
              ) : receipts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-gray-400 italic">
                    No receipts match the search criteria.
                  </td>
                </tr>
              ) : (
                receipts.map((rc) => (
                  <tr key={rc.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition">
                    <td className="p-2.5 font-black text-indigo-600 dark:text-indigo-400">
                      {rc.receiptNumber}
                    </td>
                    <td className="p-2.5 font-black text-gray-900 dark:text-white">
                      {rc.student?.studentName || "N/A"}
                    </td>
                    <td className="p-2.5 font-bold text-gray-500 dark:text-slate-400">
                      {rc.student?.admissionNumber || "N/A"}
                    </td>
                    <td className="p-2.5 font-medium text-gray-500">
                      {new Date(rc.receiptDate || rc.collectionDate || rc.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-2.5 text-right font-black text-emerald-600 dark:text-emerald-400">
                      ₹{(rc.totalAmountPaid ?? rc.amountPaid ?? 0).toLocaleString()}
                    </td>
                    <td className="p-2.5 font-bold text-gray-700 dark:text-slate-300">
                      {rc.paymentMode}
                    </td>
                    <td className="p-2.5 text-center">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${
                          rc.status === "Valid"
                            ? "bg-green-100 text-green-700 dark:bg-emerald-950 dark:text-emerald-400"
                            : rc.status === "Cancelled"
                            ? "bg-red-100 text-red-700 dark:bg-rose-950 dark:text-rose-400"
                            : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400"
                        }`}
                      >
                        {rc.status}
                      </span>
                    </td>
                    <td className="p-2.5 text-center">
                      <div className="flex justify-center gap-1.5">
                        <button
                          onClick={() => triggerPrint(rc)}
                          className="p-1.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 transition"
                          title="Re-print Receipt (Duplicate)"
                        >
                          <FaPrint size={12} />
                        </button>
                        {rc.status === "Valid" && (
                          <button
                            onClick={() => handleCancelReceipt(rc.id)}
                            className="p-1.5 text-rose-600 bg-rose-50 dark:bg-rose-950 dark:text-rose-400 rounded-lg hover:bg-rose-100 transition"
                            title="Cancel Receipt"
                          >
                            <FaBan size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hidden Layout for React-to-Print */}
      <div className="hidden">
        <PrintableFeeReceipt ref={printRef} receipt={printingReceipt} />
      </div>
    </div>
  );
}
